import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authFetch } from '../../utils/authFetch';

const EstateDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            // authFetch attaches the bearer token and transparently refreshes
            // the access token on a 401 before retrying. Only a hard auth
            // failure (no/expired refresh token) reaches the catch below.
            const response = await authFetch('/api/estates/dashboard/');
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) throw new Error('auth');
                throw new Error('network');
            }
            const data = await response.json();
            setDashboardData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
    };

    const statusColors = {
        INTAKE: 'bg-blue-100 text-blue-700',
        PROBATE: 'bg-yellow-100 text-yellow-700',
        ADMINISTRATION: 'bg-purple-100 text-purple-700',
        DISTRIBUTION: 'bg-green-100 text-green-700',
        CLOSED: 'bg-gray-100 text-gray-600',
        DISPUTED: 'bg-red-100 text-red-700',
    };

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-8">
                    <div className="h-8 w-72 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-4 w-96 bg-gray-100 rounded animate-pulse"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg animate-pulse"></div>
                                <div className="ml-4 flex-1">
                                    <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mb-2"></div>
                                    <div className="h-6 w-12 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Estate Management</h1>
                    <p className="text-gray-600 mt-1">Administer deceased estates, probate matters and trust distributions</p>
                </div>
                <div className="max-w-lg mx-auto mt-12">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
                        {error === 'auth' ? (
                            <>
                                <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Expired</h2>
                                <p className="text-gray-500 mb-6">Please sign in again to access estate management.</p>
                                <Link to="/login" className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">Sign In</Link>
                            </>
                        ) : (
                            <>
                                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Dashboard</h2>
                                <p className="text-gray-500 mb-6">We couldn't connect to the server. This is usually temporary.</p>
                                <button onClick={fetchDashboardData} className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                    Try Again
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const isEmpty = !dashboardData?.total_estates;

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Estate Management</h1>
                <p className="text-gray-600 mt-1">Administer deceased estates, probate matters and trust distributions</p>
                <p className="text-gray-400 text-xs mt-2">
                    Manage all aspects of estate administration from initial intake through final distribution, with full trust account integration.
                </p>
            </div>

            {/* Empty state onboarding */}
            {isEmpty && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                    <div className="text-center max-w-xl mx-auto">
                        <div className="mx-auto w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-5">
                            <svg className="w-10 h-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205l3 1m1.5.5l-1.5-.5M6.75 7.364V3h-3v18m3-13.636l10.5-3.819" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Estate Management</h2>
                        <p className="text-gray-500 mb-6">
                            Track deceased estates from probate through distribution. Manage assets, liabilities, beneficiaries, and link to trust accounts for fund management.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                            <Link to="/estates/new" className="inline-flex items-center px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                Create New Estate
                            </Link>
                            <Link to="/trust" className="inline-flex items-center px-5 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                                Trust Accounts
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-indigo-100 rounded-lg">
                            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Estates</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.total_estates || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Assets Value</p>
                            <p className="text-2xl font-semibold text-gray-900">{formatCurrency(dashboardData?.total_assets_value || 0)}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-red-100 rounded-lg">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Liabilities</p>
                            <p className="text-2xl font-semibold text-gray-900">{formatCurrency(dashboardData?.total_liabilities_value || 0)}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-emerald-100 rounded-lg">
                            <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Net Estate Value</p>
                            <p className="text-2xl font-semibold text-gray-900">{formatCurrency(dashboardData?.total_net_value || 0)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Link to="/estates/new" className="flex items-center justify-center p-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                    New Estate
                </Link>
                <Link to="/estates/list" className="flex items-center justify-center p-4 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                    View All Estates
                </Link>
                <Link to="/trust" className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                    Trust Accounts
                </Link>
            </div>

            {/* Status Breakdown + Recent Estates */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Status Breakdown */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="p-6 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-900">Estates by Status</h2>
                    </div>
                    <div className="p-6">
                        {dashboardData?.estates_by_status && Object.entries(dashboardData.estates_by_status).length > 0 ? (
                            <div className="space-y-3">
                                {Object.entries(dashboardData.estates_by_status).map(([status, count]) => (
                                    <div key={status} className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[status] || 'bg-gray-100 text-gray-600'}`}>
                                                {status.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-700">{count}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-center py-4">No estates yet</p>
                        )}
                    </div>
                </div>

                {/* Recent Estates */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="p-6 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Recent Estates</h2>
                            <Link to="/estates/list" className="text-sm text-indigo-600 hover:text-indigo-700">View All →</Link>
                        </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {dashboardData?.recent_estates?.length > 0 ? (
                            dashboardData.recent_estates.map((estate) => (
                                <Link key={estate.id} to={`/estates/${estate.id}`} className="block p-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-gray-900">Estate of {estate.deceased_name}</p>
                                            <p className="text-sm text-gray-500">{estate.estate_reference} • {estate.estate_type_display}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-gray-900">{formatCurrency(estate.net_estate_value)}</p>
                                            <span className={`text-xs px-2 py-1 rounded-full ${statusColors[estate.status] || 'bg-gray-100 text-gray-600'}`}>
                                                {estate.status_display}
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <div className="p-8 text-center text-gray-500">
                                <p>No estates found</p>
                                <Link to="/estates/new" className="text-indigo-600 hover:underline mt-2 inline-block">Create your first estate</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EstateDashboard;
